import { useState, useRef, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Eye, X, Building2, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Unified context banner.
 *   1. Super-admin impersonation — "Viewing as <School>" · Exit view
 *   2. Multi-school user — "Active school: <Name> ▼" switcher
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!user) return null;
  const impersonating = !!user.impersonating;
  const memberships = user.memberships || [];
  const multiSchool = memberships.length > 1;
  if (!impersonating && !multiSchool) return null;

  const exitImpersonation = async () => {
    setBusy(true);
    try {
      await api.post("/admin/exit-view-as");
      toast.success("Exited view-as mode");
      await refresh();
      window.location.href = "/admin";
    } catch (e) { toast.error("Couldn't exit"); }
    finally { setBusy(false); }
  };

  const switchSchool = async (school_id) => {
    if (school_id === user.school_id) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/switch-school/${school_id}`);
      toast.success(`Switched to ${data.school_name}`);
      await refresh();
      setOpen(false);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally { setBusy(false); }
  };

  // --- Impersonation banner (primary, attention-getting) ---
  if (impersonating) {
    return (
      <div
        data-testid="impersonation-banner"
        className="sticky top-0 z-50 w-full bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              Viewing as <strong className="font-semibold">{user.impersonating_school_name}</strong> school admin
            </span>
          </div>
          <button
            onClick={exitImpersonation}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1 rounded-sm bg-white/15 hover:bg-white/25 transition uppercase tracking-wider text-xs font-semibold disabled:opacity-50"
            data-testid="exit-impersonation-btn"
          >
            <X className="w-3 h-3" /> Exit view
          </button>
        </div>
      </div>
    );
  }

  // --- Multi-school switcher (quiet, top strip) ---
  const active = memberships.find((m) => m.school_id === user.school_id) || memberships[0];
  return (
    <div
      data-testid="school-switcher-banner"
      ref={ref}
      className="sticky top-0 z-50 w-full bg-slate-100 border-b border-slate-200 text-slate-700"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-3 text-sm relative">
        <Building2 className="w-4 h-4 flex-shrink-0 text-[var(--brand-primary)]" />
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-white transition"
          data-testid="school-switcher-toggle"
        >
          <span className="text-slate-500">Active school:</span>
          <strong className="font-semibold text-slate-900">{active?.school_name || "—"}</strong>
          {active?.role && (
            <span className="status-badge status-brand">{active.role}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <span className="ml-auto hidden sm:block text-xs text-slate-500">
          You&apos;re in {memberships.length} schools — switch anytime
        </span>

        {open && (
          <div
            className="absolute left-4 top-full mt-1 w-72 bg-white border border-slate-200 rounded-sm shadow-lg z-50 py-1"
            data-testid="school-switcher-menu"
          >
            {memberships.map((m) => {
              const isActive = m.school_id === user.school_id;
              return (
                <button
                  key={m.school_id}
                  onClick={() => switchSchool(m.school_id)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition flex items-center gap-3"
                  data-testid={`switch-to-${m.school_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate text-sm">{m.school_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mt-0.5">
                      {m.role}{m.school_icao ? ` · ${m.school_icao}` : ""}
                    </div>
                  </div>
                  {isActive && <Check className="w-4 h-4 text-[var(--brand-primary)]" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
